import { MIN_POSITION_USD_THRESHOLD } from '@/components/dashboard/getPositionsAboveThreshold'
import { normalizeBorrowPositionUsdValue } from '@/utils/borrowMarkets/normalizeBorrowPositionUsdValue'
import { type BorrowMarketPosition, type BorrowPositionAsset, getInternalAppSdk } from '@/utils/internalAppSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import type { Address } from 'viem'

export const borrowPositionsQueryKey = ['borrowPositions'] as const

interface BorrowPositionsQueryParams {
  address: Address | undefined
}

export interface BorrowPositionAssetBalance
  extends Omit<BorrowPositionAsset, 'balanceUsd' | 'suppliedUsd' | 'borrowedUsd'> {
  balanceUsd: number
  suppliedUsd: number
  borrowedUsd: number
}

export interface BorrowPosition extends Omit<BorrowMarketPosition, 'assets'> {
  assets: BorrowPositionAssetBalance[]
  suppliedUsd: number
  borrowedUsd: number
  netUsd: number
}

export interface BorrowPositionsTotals {
  suppliedUsd: number
  borrowedUsd: number
  netUsd: number
}

export const getBorrowPositionsTotals = (
  borrowPositions: BorrowPosition[] | null | undefined
): BorrowPositionsTotals => {
  return (
    borrowPositions?.reduce(
      (totals, position) => ({
        suppliedUsd: totals.suppliedUsd + position.suppliedUsd,
        borrowedUsd: totals.borrowedUsd + position.borrowedUsd,
        netUsd: totals.netUsd + position.netUsd,
      }),
      { suppliedUsd: 0, borrowedUsd: 0, netUsd: 0 }
    ) ?? { suppliedUsd: 0, borrowedUsd: 0, netUsd: 0 }
  )
}

function borrowPositionsQueryOptions({ address }: BorrowPositionsQueryParams) {
  return queryOptions({
    queryKey: [...borrowPositionsQueryKey, { userAddress: address }] as const,
    async queryFn({ queryKey }) {
      const { userAddress } = queryKey[1]
      if (!userAddress) return null
      const internalSdk = getInternalAppSdk()
      return await internalSdk.getBorrowPositions(userAddress, {
        minUsdAssetValueThreshold: MIN_POSITION_USD_THRESHOLD,
      })
    },
    select(data): BorrowPosition[] | null {
      if (data == null) {
        return null
      }

      return data.data.map((position) => {
        const assets = position.assets.map((asset) => ({
          ...asset,
          balanceUsd: normalizeBorrowPositionUsdValue(asset.balanceUsd),
          suppliedUsd: normalizeBorrowPositionUsdValue(asset.suppliedUsd),
          borrowedUsd: normalizeBorrowPositionUsdValue(asset.borrowedUsd),
        }))
        const suppliedUsd = assets.reduce((acc, asset) => acc + asset.suppliedUsd, 0)
        const borrowedUsd = assets.reduce((acc, asset) => acc + asset.borrowedUsd, 0)

        return {
          ...position,
          assets,
          suppliedUsd,
          borrowedUsd,
          netUsd: suppliedUsd - borrowedUsd,
        }
      })
    },
    enabled: !!address,
    retry: 2,
    staleTime: 1 * MINUTE_IN_MILLISECONDS,
    refetchOnWindowFocus: true,
    placeholderData: undefined,
  })
}

export const useBorrowPositionsQuery = ({ address }: BorrowPositionsQueryParams) => {
  const { data, isError, isFetching } = useQuery({
    ...borrowPositionsQueryOptions({ address }),
  })
  return { data, isError, isFetching }
}
